import { useEffect } from 'react'
import { connect } from 'react-redux'
import { reduxForm, ReduxFormProps } from 'redux-form'
import { createValidator, required } from 'lib/form-validation'
import { Form } from 'components/forms'
import { SaveButton } from 'components/buttons'
import ConsolidatedShippingFormFields, {
  ConsolidatedShippingSubtext,
} from 'registry/forms/consolidated-shipping-form-fields'
import { StyledSubHeading } from 'components/headings'
import {
  getShippingConsolidation,
  getRegistryId,
  getRegistryOwnerId,
} from 'registry/reducers'
import { saveConsolidationChoice } from 'registry/actions'
import { useTracking, track } from 'lib/analytics'

interface Values {
  consolidationChoice: string
}

type FormProps = Pick<ReduxFormProps<Values>, 'submitting' | 'fields'>

interface ComponentProps {
  dispatch: any
  handleSubmit: any
  currentConsolidationChoice: string | null
  registryId: number
  registryOwnerId: number
}

type ConsolidatedShippingProps = ComponentProps & FormProps

const ConsolidatedShipping = ({
  fields,
  submitting,
  handleSubmit,
  dispatch,
  currentConsolidationChoice,
  registryId,
  registryOwnerId,
}: ConsolidatedShippingProps) => {
  const onSave = (values: Values) => {
    if (values.consolidationChoice != currentConsolidationChoice) {
      tracker.trackEvent({
        event: track.shippingConsolidationUpdated,
        consolidationValue: values.consolidationChoice,
        ...eventProps,
      })
      return dispatch(saveConsolidationChoice(values.consolidationChoice))
    }
  }
  const tracker = useTracking()
  const eventProps = {
    eventLocation: track.EventLocation.SETTINGS_MINUS_SHIPPING_CONSOLIDATION,
    registryId,
    registryOwnerId,
  }

  // track if showing the consolidation choice
  useEffect(() => {
    tracker.trackEvent({
      event: track.shippingConsolidationViewed,
      ...eventProps,
    })
    const { initialValue, onChange } = fields.consolidationChoice
    onChange(initialValue)
  }, [])

  return (
    <Form onSubmit={handleSubmit(onSave)}>
      <StyledSubHeading>Registry Shipping Preferences</StyledSubHeading>
      <ConsolidatedShippingFormFields
        consolidationChoice={fields.consolidationChoice}
      />
      <hr />
      <SaveButton submitting={submitting} className="mtm">
        Save
      </SaveButton>
      <div className="mtl">
        <ConsolidatedShippingSubtext />
      </div>
    </Form>
  )
}

const mapStateToProps = (state: any) => ({
  currentConsolidationChoice:
    getShippingConsolidation(state)?.consolidationChoice,
  registryId: getRegistryId(state),
  registryOwnerId: getRegistryOwnerId(state),
})

const mapDispatchToProps = () => ({
  onSubmitSuccess: () => {
    PubSub.publish(BLConstants.FLASH_MESSAGE, {
      variant: 'success',
      message: 'Your Shipping Preferences setting has been updated',
    })
  },
  onSubmitFail: () => {
    PubSub.publish(BLConstants.FLASH_MESSAGE, {
      variant: 'Error',
      message: 'Your Shipping Preferences setting could not be saved',
    })
  },
})

const ConsolidatedShippingForm = reduxForm(
  {
    form: 'ConsolidatedShippingForm',
    fields: ['consolidationChoice'],
    validate: createValidator({ consolidationChoice: [required] }),
  },
  (state: any) => {
    const consolidationChoice =
      getShippingConsolidation(state)?.consolidationChoice

    return {
      initialValues: {
        consolidationChoice,
      },
    }
  }
)(ConsolidatedShipping as any) // need to pass as any as the reduxForm type defintion doesn't support a functional component

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ConsolidatedShippingForm)
